import React, { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { setFidoAuthenticators } from '@theme/js/components/main/NevisPasskeysSwiss/server';
import { useTranslations } from '@core/js/hooks/i18n';
import { FidoAuthenticator, INevisPopupHandlers } from '@theme/js/components/main/NevisPasskeysSwiss/types';

export const useEventHandlerForNevisPopup = ({
    fidoAuthenticators,
    index,
    fetchPortlet,
    portletType,
    closePopup,
    nameAuth
}: INevisPopupHandlers) => {
    const [isAbove, setLabelPosition] = useState<boolean>(true);
    const [isDisabled, setDisabledState] = useState<boolean>(true);
    const [isFocus, setFocus] = useState<boolean>(true);
    const [isError, setErrorState] = useState<boolean>(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const t = useTranslations();
    const placeholder = t('playtech.mobile.nevis-passkeys-swiss.passkeys.passkey.name.placeholder');

    const onAccept = useCallback(async () => {
        const updatedFidoAuthenticators = Object.values(fidoAuthenticators).reduce((acc: FidoAuthenticator[], cur, i) => {
            if (i === index) {
                acc[i] = { ...cur, name: inputRef?.current?.value ?? '' };
            } else {
                acc[i] = cur;
            }
            return acc;
        }, []);
        try {
            await setFidoAuthenticators(updatedFidoAuthenticators);
            await fetchPortlet({ type: portletType }, { showLoader: false });
            closePopup();
        } catch (error: any) {
            const { data = {} } = error?.response || error || {};
            console.error(data);
        }
    }, [fidoAuthenticators, index, inputRef, fetchPortlet, portletType, closePopup]);

    const onAcceptButton = useCallback((e: React.MouseEvent<HTMLButtonElement> | any) => {
        e.stopPropagation();
        if (!isError && inputRef?.current?.value !== nameAuth) {
            onAccept();
        }
    }, [inputRef, isError, nameAuth, onAccept]);

    const checkRegExp = useCallback((target:string) => {
        const regExp = new RegExp('^(?![a-zA-Z0-9# _-]{3,25}$).*$', 'g');
        setErrorState(regExp.test(target));
    }, [setErrorState]);

    const onBlur = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setLabelPosition(inputRef?.current?.value !== '');
        if (isDisabled) {
            e.target.placeholder = '';
        } else {
            checkRegExp(e.target.value);
        }
        setFocus(false);
    }, [checkRegExp, inputRef, isDisabled, setFocus, setLabelPosition]);

    const onFocus = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setLabelPosition(true);
        setErrorState(false);
        if (isDisabled) {
            e.target.placeholder = placeholder;
        }
        setFocus(true);
    }, [isDisabled, placeholder, setErrorState, setFocus, setLabelPosition]);

    const onChange = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        e.preventDefault();
        const target = e.target as HTMLInputElement;
        if (target.value && target.value !== nameAuth) {
            setDisabledState(false);
        } else {
            if (!target.value) {
                target.placeholder = placeholder;
            }
            setDisabledState(true);
        }
    }, [nameAuth, placeholder, setDisabledState]);

    const clickOnInvalidIcon = useCallback(() => inputRef?.current?.focus(), [inputRef]);

    useLayoutEffect(() => {
        if (inputRef?.current) {
            inputRef?.current.setAttribute('value', nameAuth);
            inputRef.current.focus();
            const positionCursor = inputRef.current.value.length;
            inputRef.current.setSelectionRange(positionCursor, positionCursor);
        }
    }, [nameAuth]);

    return {
        isError,
        isAbove,
        isFocus,
        isDisabled,
        inputRef,
        onChange,
        onAccept,
        checkRegExp,
        onBlur,
        onFocus,
        clickOnInvalidIcon,
        onAcceptButton
    };
};
